import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Select, Avatar } from "antd";
import { ShopOutlined, UserOutlined } from "@ant-design/icons";
import { getFranchiseManagerList } from "../../Redux/Actions/franchiseManagerAction";
import { AssignFranchiseManager } from "../../Redux/Actions/agentActions";
import toast from "react-hot-toast";

const NAVY = "#12121f";

const AssignFranchiseModal = ({ visible, record, onClose, onAssigned }) => {
  const dispatch = useDispatch();
  const [selectedFranchiseId, setSelectedFranchiseId] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);

  const { franchiseManagers } = useSelector((state) => state.franchiseManager || {});

  useEffect(() => {
    if (visible) {
      dispatch(getFranchiseManagerList({ page: 1, limit: 100, role: "franchise manager" }, false));
      setSelectedFranchiseId(record?.franchise_id?._id || record?.franchise_id || undefined);
    }
  }, [visible, record, dispatch]);

  const handleOk = async () => {
    if (!selectedFranchiseId) {
      toast.error("Please select a franchise manager");
      return;
    }
    setSubmitting(true);
    try {
      await dispatch(AssignFranchiseManager({ id: record._id, franchise_id: selectedFranchiseId }));
      onAssigned && onAssigned();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const options = (Array.isArray(franchiseManagers) ? franchiseManagers : []).map((fm) => ({
    label: fm.name,
    value: fm._id,
  }));

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={420}
      closeIcon={<span style={{ fontSize: 16, color: "#8c8c8c" }}>✕</span>}
      styles={{ body: { padding: "4px 0 0" } }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: 10,
            background: NAVY,
            color: "#fff",
            fontSize: 18,
          }}
        >
          <ShopOutlined />
        </span>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1f1f1f" }}>Assign Franchise Manager</div>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>Link this agent to a franchise manager</div>
        </div>
      </div>

      {/* Agent card */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 16px",
          background: "#fafafa",
          border: "1px solid #ececec",
          borderRadius: 12,
          marginBottom: 22,
        }}
      >
        <Avatar
          size={42}
          style={{
            background: NAVY,
            color: "#fff",
            fontWeight: 600,
            fontSize: 16,
          }}
        >
          {record?.name ? record.name.charAt(0).toUpperCase() : "?"}
        </Avatar>
        <div style={{ lineHeight: 1.35, overflow: "hidden" }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#1f1f1f" }}>{record?.name || "—"}</div>
          <div
            style={{
              fontSize: 12,
              color: "#8c8c8c",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {record?.email || record?.staff_id}
          </div>
        </div>
        {record?.staff_id && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: 11,
              fontWeight: 500,
              color: "#595959",
              border: "1px solid #d9d9d9",
              borderRadius: 20,
              padding: "3px 10px",
              whiteSpace: "nowrap",
            }}
          >
            {record.staff_id}
          </span>
        )}
      </div>

      {/* Select */}
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "#434343",
          marginBottom: 8,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <UserOutlined style={{ color: "#8c8c8c" }} />
        Franchise Manager
      </div>
      <Select
        style={{ width: "100%" }}
        size="large"
        placeholder="Select franchise manager"
        value={selectedFranchiseId}
        onChange={(value) => setSelectedFranchiseId(value)}
        options={options}
        showSearch
        optionFilterProp="label"
        notFoundContent="No franchise managers found"
      />

      {record?.franchise_id && (
        <div
          style={{
            marginTop: 14,
            padding: "10px 12px",
            background: "#fffbe6",
            border: "1px solid #fff1b8",
            borderRadius: 10,
            fontSize: 12,
            color: "#8c6d1f",
          }}
        >
          This agent already has a franchise manager assigned — selecting a different one will reassign them.
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          marginTop: 24,
          paddingTop: 16,
          borderTop: "1px solid #f0f0f0",
        }}
      >
        <button
          onClick={onClose}
          style={{
            padding: "8px 20px",
            borderRadius: 20,
            border: "1px solid #d9d9d9",
            background: "#fff",
            color: "#434343",
            fontWeight: 500,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleOk}
          disabled={!selectedFranchiseId || submitting}
          style={{
            padding: "8px 22px",
            borderRadius: 20,
            border: "none",
            background: NAVY,
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            cursor: !selectedFranchiseId || submitting ? "not-allowed" : "pointer",
            opacity: !selectedFranchiseId || submitting ? 0.6 : 1,
          }}
        >
          {submitting ? "Assigning..." : "Assign"}
        </button>
      </div>
    </Modal>
  );
};

export default AssignFranchiseModal;